import Markdown from "markdown-to-jsx";
import React from "react";
import { twMerge } from "tailwind-merge";
import { ContentBlockTeamGridLayoutCardsType } from "../../../types/blocks";
import Image from "../../atoms/image";
import Link from "../../atoms/link";
import IconGithub from "../../icons/github";
import IconLinkedin from "../../icons/linkedin";
import IconTwitter from "../../icons/twitter";
import CTAs from "../../molecules/ctas";

const icons: { [key: string]: React.ReactNode } = {
  twitter: <IconTwitter />,
  linkedin: <IconLinkedin />,
  github: <IconGithub />,
};

const columnsClasses: { [key: string]: string } = {
  "2": "sm:grid-cols-2",
  "3": "sm:grid-cols-2 lg:grid-cols-3",
  "4": "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
};

const columnsContainerClasses: { [key: string]: string } = {
  "2": "max-w-screen-lg",
  "3": "max-w-screen-xl",
  "4": "max-w-screen-xl",
};

const TeamGridLayoutCards = ({
  headline,
  copytext,
  columns,
  team_members,
  ctas,
  block_settings: { background_color, html_id } = {
    background_color: "bg-white dark:bg-secondary-900",
  },
}: Omit<ContentBlockTeamGridLayoutCardsType, "type">) => {
  return (
    <section className={twMerge(background_color, "scroll-mt-20")} id={html_id}>
      <div
        className={twMerge(
          "px-4 py-8 mx-auto lg:py-16 lg:px-6 space-y-8 lg:space-y-16",
          columnsContainerClasses[columns]
        )}
      >
        <div className="max-w-screen-sm mx-auto text-center">
          <h2 className="mb-4 text-4xl font-extrabold tracking-tight text-secondary-900 dark:text-white">
            <Markdown>{headline}</Markdown>
          </h2>
          <p className="font-normal text-secondary-500 lg:mb-16 md:text-lg dark:text-secondary-400">
            {copytext}
          </p>
        </div>
        <div className={twMerge("grid gap-6", columnsClasses[columns])}>
          {team_members.map((member) => (
            <div
              key={member.name}
              className="bg-white border border-gray-200 shadow-sm rounded-xl dark:bg-secondary-800 dark:border-gray-700"
            >
              <div className="p-4">
                <Image
                  image={member.image}
                  alt={member.name}
                  className="w-full overflow-hidden rounded-lg aspect-square"
                />
              </div>

              <div className="px-5 pb-5">
                <h3 className="text-xl font-bold tracking-tight text-secondary-900 dark:text-white">
                  {member.name}
                </h3>
                <p className="text-secondary-500">{member.position}</p>
                <p className="text-secondary-500">{member.superpower}</p>
                <div className="mt-3 mb-4 text-secondary-500 dark:text-secondary-400">
                  <Markdown className="space-y-3 font-normal">
                    {member.description}
                  </Markdown>
                </div>
                <ul className="flex space-x-4 sm:mt-0">
                  {member.socials.map((social) => (
                    <li key={social.target}>
                      <Link
                        size="none"
                        to={social.target}
                        className="text-secondary-500 hover:text-secondary-900 dark:hover:text-white"
                      >
                        {icons[social.icon]}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
        {ctas.length > 0 && <CTAs ctas={ctas} className="justify-center" />}
      </div>
    </section>
  );
};

export default TeamGridLayoutCards;
